import { useLazyQuery, useQuery } from '@apollo/client';
import { Button, Form, Input, theme } from 'antd';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  StaffEntitySearchContainerEntityResultFieldsFragment,
  StaffEntitySearchContainerEntitySearchDocument,
  StaffEntitySearchContainerEntitySearchFacetsFieldsFragment,
  StaffEntitySearchContainerStaffUserCurrentDocument
} from '../../../../generated.tsx';
import { useOid } from '../../../../hooks/useOid.ts';
import { Country } from '../../../../types/country.ts';
import { ComponentQueryLoader } from '../../../shared/component-query-loader.tsx';
import { useSearchState } from '../../../shared/use-search-state';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled } from '../../../shared/utils/queryOptions';
import { CheckFilterInterface, EntitySearchFilterParamKey } from './entity-search-filter.tsx';
import { EntitySearchTable } from './entity-search-table.tsx';
import { lookupCountryQuery } from './staff-components-country-query.tsx';

interface EntitySearchContainerProps {
  filters: CheckFilterInterface;
  setFilterDataCount: React.Dispatch<React.SetStateAction<StaffEntitySearchContainerEntitySearchFacetsFieldsFragment>>;
  isMobile: boolean;
}

export const EntitySearchContainer: React.FC<EntitySearchContainerProps> = (props) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled(oid);

  const {
    token: { colorBgContainer }
  } = theme.useToken();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { inputValue, setInputValue, handleSearchSubmit } = useSearchState();

  // const [inputValue, setInputValue] = useState<string>('');

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  const getSelectedFiltersFromSearchParams = (includeParams: string[] = []) => {
    const filters: any = {};
    for (let [key, value] of searchParams.entries()) {
      if (!includeParams.includes(key)) {
        continue;
      }

      if (key === EntitySearchFilterParamKey.isClient || key === EntitySearchFilterParamKey.isIssuingInstitution) {
        console.log('key', key);
        filters[key] = value === 'true';
        continue;
      } else {
        filters[key] = value.split(',');
      }
    }
    return filters;
  };

  const [doSearch, { data: entityList, loading: entityListLoading, error: entityListError }] = useLazyQuery(StaffEntitySearchContainerEntitySearchDocument, {
    fetchPolicy: 'cache-and-network'
  });

  const { data: currentStaffData } = useQuery(StaffEntitySearchContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const onSearchStringClear = () => {
    searchParams.delete('search');
    setInputValue('');
    handleSearchSubmit('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    const page = searchParams.get('page') ? parseInt(searchParams.get('page') as string, 10) : 1;
    const pageSize = searchParams.get('pageSize') ? parseInt(searchParams.get('pageSize') as string, 10) : 10;
    const filterOption = getSelectedFiltersFromSearchParams(
      Object.keys(EntitySearchFilterParamKey).filter((key) => key !== EntitySearchFilterParamKey.isClient && key !== EntitySearchFilterParamKey.isIssuingInstitution)
    );
    if (!(searchParams.get(EntitySearchFilterParamKey.isClient)?.includes('true') && searchParams.get(EntitySearchFilterParamKey.isClient)?.includes('false'))) {
      filterOption[EntitySearchFilterParamKey.isClient] = searchParams.get(EntitySearchFilterParamKey.isClient)?.includes('true');
    }
    if (
      !(
        searchParams.get(EntitySearchFilterParamKey.isIssuingInstitution)?.includes('true') &&
        searchParams.get(EntitySearchFilterParamKey.isIssuingInstitution)?.includes('false')
      )
    ) {
      filterOption[EntitySearchFilterParamKey.isIssuingInstitution] = searchParams.get(EntitySearchFilterParamKey.isIssuingInstitution)?.includes('true');
    }
    doSearch({
      variables: {
        input: {
          options: {
            filter: filterOption,
            skip: (page - 1) * pageSize,
            top: pageSize
          },
          searchString: searchParams.get('search') || ''
        }
      }
    });
  }, [searchParams]);

  useEffect(() => {
    if (entityList) {
      props?.setFilterDataCount(entityList?.entitySearch?.facets as StaffEntitySearchContainerEntitySearchFacetsFieldsFragment);
    }
  }, [entityList, props?.setFilterDataCount]);

  useEffect(() => {
    const search = searchParams.get('search') || '';
    setInputValue(search);
  }, []);
  const canAddEntities = currentStaffData?.staffUserCurrent?.role?.permissions?.entityPermissions?.canAddEntities;

  return (
    <div
      className={`
      ${props.isMobile ? 'pl-0' : 'pl-12'} 
      bg-[${colorBgContainer}] min-w-[300px] w-full p-12 pt-0 rounded-lg flex-[1_1_30%]
    `}
    >
      <Form
        layout="inline"
        onFinish={() => {
          handleSearchSubmit(inputValue);
        }}
        className="mb-1"
      >
        <Form.Item>
          <Input
            maxLength={200}
            value={inputValue}
            allowClear
            onClear={onSearchStringClear}
            onChange={handleInputChange}
            className="min-w-20 sm:min-w-80 border border-black mb-2"
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" className="ml-0 mr-5">
            Search
          </Button>
        </Form.Item>
        <Form.Item style={{ marginLeft: 'auto' }}>
          <Button type="primary" className="ml-0" onClick={() => navigate('create')} disabled={!canAddEntities}>
            Create Entity
          </Button>
        </Form.Item>
      </Form>

      <ComponentQueryLoader
        loading={entityListLoading || countryLoading}
        hasData={entityList}
        error={entityListError || countryError}
        hasDataComponent={
          <EntitySearchTable
            resultCount={entityList?.entitySearch?.count ?? 0}
            data={entityList?.entitySearch?.entityResults as StaffEntitySearchContainerEntityResultFieldsFragment[]}
            countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          />
        }
      />
    </div>
  );
};
